import { Locale } from '@core/constants/locales.constants';
import { VideoLandingPageVideoHeroItem } from '@graphql/index';
import { Translations } from '@lib/dictionary-keys/__generated__';
import {
  Flex,
  HeroHeaderBlockProps,
  ThumbnailCardProps,
} from '@stadionhq-v3/base-ui';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import { ArrowRight, Lock, Play } from '@components/icons';
import templateConfig from '@templateConfig';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import Image from 'next/image';
import { Button } from '@components/v3/atoms/button';
import { CloudMatrixSearchResponse, ItemData } from '@stadionhq-v3/stream-api';
import { VIDEO_PUBLISH_DATE_FORMAT } from '@core/constants/video.constants';
import {
  getVideoDescription,
  getVideoTitle,
} from '@utils/transforms/video/videoFieldFallbacks';
import { linkTransform } from '../atoms';

const videoLandingHeroTransform = (
  videos: CleanContentful<VideoLandingPageVideoHeroItem>[],
  cloudMatrixData: CloudMatrixSearchResponse[] | undefined,
  locale: Locale,
  dictionary: {
    watchVideoArticle: Translations['video']['label']['watchNow'];
    membersOnly: Translations['videoCard']['label']['membersOnly'];
  }
): HeroHeaderBlockProps => ({
  heroItems: videos
    ?.filter((video) => video != null)
    .map((video, index) => {
      const isVideo = video.__typename === 'Video';
      const isItemHero = video.__typename === 'ItemHeroSlide';

      const primaryLink =
        isItemHero &&
        video.primaryLink &&
        (video?.primaryLink?.url || video?.primaryLink.internalLinkData)
          ? linkTransform(video.primaryLink, locale)
          : undefined;

      const secondaryLink =
        isItemHero &&
        video.secondaryLink &&
        (video?.secondaryLink?.url || video?.secondaryLink.internalLinkData)
          ? linkTransform(video.secondaryLink, locale)
          : undefined;

      const tertiaryLink =
        isItemHero &&
        video.tertiaryLink &&
        (video?.tertiaryLink?.url || video?.tertiaryLink.internalLinkData)
          ? linkTransform(video.tertiaryLink, locale)
          : undefined;

      const videoCloudMatrixData: ItemData | undefined =
        video.__typename === 'Video' &&
        video.video.provider.startsWith('stream')
          ? cloudMatrixData?.[index]?.itemData[0]
          : undefined;

      const withEntitlements =
        videoCloudMatrixData?.metaData?.SysEntryEntitlements.length > 0;

      return {
        id: video.id,
        withPublishDate: true,
        preTitleText: isItemHero && video.slideSectionPreTitleText,
        title: isVideo ? getVideoTitle(video) : video.title,
        summary: isVideo && getVideoDescription(video),
        publishDate: isVideo &&
          video.video.date && {
            date: video.video.date,
            format: VIDEO_PUBLISH_DATE_FORMAT,
            relativeTimeUnit: 'week',
          },
        category:
          isVideo && videoCloudMatrixData?.metaData.category
            ? {
                title: videoCloudMatrixData?.metaData.category,
                category: videoCloudMatrixData?.metaData.category,
                slug: '#',
                disabled: true,
              }
            : isItemHero
              ? {
                  title: video.slideSectionPreTitleText,
                  category: video.slideSectionPreTitleText,
                  slug: '#',
                  disabled: true,
                }
              : undefined,
        // sponsor: isVideo &&
        //   article.sponsor && {
        //     sponsorLabel: dictionary.sponsoredBy,
        //     onDark: true,
        //     image: (
        //       <ContentfulImage
        //         height={article.sponsor.height as number}
        //         width={article.sponsor.width as number}
        //         url={article.sponsor?.url as string}
        //         alt={article.sponsor?.title as string}
        //       />
        //     ),
        //   },
        backgroundCarouselOptions: {
          type: 'fade',
          rewindSpeed: 1000,
          drag: false,
          speed: 1000,
        },
        membersBadge: withEntitlements && {
          leftElement: <Lock />,
          text: dictionary.membersOnly,
        },
        image: video?.image ? (
          <ContentfulImage {...video.image} />
        ) : (
          <Image
            alt="Thumbnail"
            layout="fill"
            objectFit="cover"
            src={
              isVideo
                ? video.video?.thumbnailImageUrl
                : '/images/player-profile-card-fallback-bg.png'
            }
          />
        ),
        ctaButton: isVideo ? (
          <Button
            {...{
              text: dictionary?.watchVideoArticle,
              url: generateFullPathname({
                locale,
                route: templateConfig.navigation.routes.VIDEO_DETAIL,
                params: { slug: video?.slug },
              }),
              variant: 'primary',
              onDark: true,
              iconBefore: <Play />,
            }}
          />
        ) : (
          isItemHero && (
            <Flex
              flexDirection="row"
              flexWrap="wrap"
              flexGrow={1}
              tokens={{
                gap: '--s-spacer-6',
              }}
            >
              {primaryLink && (
                <Button
                  {...{
                    type: 'button',
                    text: primaryLink.title,
                    url: primaryLink.url,
                    target: primaryLink.target,
                    openInNewTab: primaryLink.openInNewTab,
                    variant: 'primary',
                    onDark: true,
                    iconAfter: <ArrowRight />,
                  }}
                />
              )}
              {secondaryLink && (
                <Button
                  {...{
                    type: 'button',
                    text: secondaryLink.title,
                    url: secondaryLink.url,
                    target: secondaryLink.target,
                    openInNewTab: secondaryLink.openInNewTab,
                    variant: 'secondary',
                    onDark: true,
                    iconAfter: <ArrowRight />,
                  }}
                />
              )}
              {tertiaryLink && (
                <Button
                  {...{
                    type: 'button',
                    text: tertiaryLink.title,
                    url: tertiaryLink.url,
                    target: tertiaryLink.target,
                    openInNewTab: tertiaryLink.openInNewTab,
                    variant: 'tertiary',
                    onDark: true,
                    iconAfter: <ArrowRight />,
                  }}
                />
              )}
            </Flex>
          )
        ),
      };
    }) as ThumbnailCardProps[],
  heroPagination: {
    interactive: true,
    variant: 'article',
  },
});

export default videoLandingHeroTransform;
